import { IUserDto, userDto } from 'src/assets/common/models/user-dto.g';
import { affiliation, TAffiliation } from 'src/assets/common/enums/affiliation.g';

// Naamregels van een User — spiegelt UserNameHelper/User in de backend, wijzig beide samen.
// Alle naamvelden zijn optioneel (privacy: technisch is alleen het e-mailadres nodig):
//   firstName/lastName  formele naam (facturatie, formele documenten)
//   applicationName     naam zichtbaar voor anderen in de applicatie; wordt niet automatisch gevuld
//   correspondenceName  aanhef in mails (alleen backend, User.MailName)
// Terugvalketens, allemaal eindigend op het e-mailadres:
//   userDisplayName:  applicationName → firstName + lastName → email
//   displayName (DomainUser): domainUser.name → userDisplayName  (domainUserDisplayName)
// Weergave "naam (email)": nameWithEmail (tekst) of emailSuffix (alleen het e-mailadres, voor een
// aparte opmaak in templates via de emailSuffix-pipe).
// Identificatie van een lid in lijsten: "naam · organisatie" (organisationLabel). Het e-mailadres is contactinformatie,
// geen identificatie: het verschilt per kijker (privacy), de organisatie niet.

export const organisationLabel_own = 'intern';

// Organisatie van een lid (DomainUser.Affiliation): eigen organisatie = 'intern', een contact = de contactnaam,
// onbekend = null.
export function organisationLabel(value: TAffiliation | null | undefined, contactName: string | null | undefined): string | null {
    if (value === affiliation.own) return organisationLabel_own;
    if (value === affiliation.contact) return contactName?.trim() || null;
    return null;
}

// Spiegelt UserNameHelper.Name_compose: firstName + lastName, met spatie alleen als beide gevuld zijn.
export function userNameCompose(firstName: string | null | undefined, lastName: string | null | undefined): string {
    return [firstName, lastName]
        .map(value => value?.trim())
        .filter(value => !!value)
        .join(' ');
}

// Spiegelt UserNameHelper.FirstFilled: eerste niet-lege waarde (getrimd), anders ''.
export function firstFilled(...values: (string | null | undefined)[]): string {
    for (const __value of values) {
        const __trimmed = __value?.trim();
        if (__trimmed) return __trimmed;
    }
    return '';
}

// Spiegelt UserNameHelper.Name_withEmail: "naam (email)". Een displayName is niet uniek, het e-mailadres wel.
// Zonder e-mail of als de naam al het e-mailadres is (einde van de keten): alleen de naam.
export function nameWithEmail(name: string | null | undefined, email: string | null | undefined): string {
    const __name = name?.trim() || '';
    const __suffix = emailSuffix(name, email);
    if (!__suffix) return __name;
    return __name ? `${__name} (${__suffix})` : __suffix;
}

// Het e-mailadres dat achter een naam hoort ("naam (email)"), of '' als er geen e-mail is of de naam al
// het e-mailadres is (einde van de keten). Zelfde regel als nameWithEmail; voor templates die het
// e-mailadres apart opmaken (emailSuffix-pipe, class email-suffix).
export function emailSuffix(name: string | null | undefined, email: string | null | undefined): string {
    const __name = name?.trim() || '';
    const __email = email?.trim() || '';
    if (!__email || __name.toLowerCase() === __email.toLowerCase()) return '';
    return __email;
}

// Spiegelt DomainUser.DisplayName: DomainUser.Name (optioneel, vaak leeg) → userDisplayName. Eén functie voor
// alle modellen met een DomainUser (DomainUser, FunctionMember, ProjectMember — enkel én lijst), zodat een
// model niet stil terugvalt op de lege DomainUser.Name.
export function domainUserDisplayName(domainUserName: string | null | undefined, userName: string | null | undefined): string {
    return firstFilled(domainUserName, userName);
}

// Spiegelt User.DisplayName: applicationName → firstName + lastName → email.
export function userDisplayName(user: IUserDto | null | undefined): string {
    return firstFilled(
        user?.[userDto.applicationName],
        userNameCompose(user?.[userDto.firstName], user?.[userDto.lastName]),
        user?.[userDto.email],
    );
}
